import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Layers, FileCheck2, ArrowRight, Phone, Mail } from "lucide-react";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";

const STATS = [
  { k: "< 2 hrs", v: "Referral acknowledgement" },
  { k: "VIC · WA", v: "Melbourne & Perth operations" },
  { k: "24/7", v: "On-call & SIL capacity" },
  { k: "NDIS", v: "Registered & audited" },
];

const WHY = [
  { Icon: ShieldCheck, title: "Registered & compliant", text: "NDIS registered; operating under the NDIS Practice Standards with documented safeguarding." },
  { Icon: Clock, title: "Responsive", text: "Referrals acknowledged within 2 business hours. Capacity confirmed within 48 hours." },
  { Icon: Layers, title: "Broad capability", text: "Daily living, SIL (incl. forensic overlay), SDA, community nursing, MTA, STA and coordination." },
  { Icon: FileCheck2, title: "Clean handovers", text: "Documentation, risk, medication and clinical notes — done properly and shared with you." },
];

export default function ForCoordinatorsPage() {
  useSEO({
    title: "For Support Coordinators & Referrers | Melbourne & Perth NDIS Provider | Alpha Community Care",
    description: "A registered NDIS provider Melbourne and Perth support coordinators rely on for complex SIL, SDA, forensic overlay and community nursing placements. Referrals acknowledged within 2 business hours.",
    keywords: ["NDIS referral Melbourne", "NDIS referral Perth", "support coordinator provider", "complex SIL provider", "forensic disability referral", "SDA vacancies coordinator"],
    canonical: `${SITE_URL}/for-support-coordinators`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "For Support Coordinators", path: "/for-support-coordinators" }])],
  });
  return (
    <div data-testid="page-for-coordinators">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="alpha-overline">For support coordinators & referrers</div>
            <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">A provider that respects your workflow.</h1>
            <p className="mt-5 text-lg text-[color:var(--alpha-mute)] max-w-xl">Fast intake. Clean documentation. Active clinical and behavioural oversight. Alpha Community Care is a registered NDIS provider that coordinators across Melbourne and Perth rely on for complex, high-quality placements.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/referrals/coordinator" className="alpha-btn-primary" data-testid="fc-cta-refer">Submit a referral <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/vacancies" className="alpha-btn-ghost">Browse live vacancies</Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {STATS.map((s) => (
              <div key={s.v} className="alpha-card p-5">
                <div className="font-display text-2xl font-semibold text-[color:var(--alpha-ink)]">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--alpha-mute)] mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((c) => (
            <div key={c.title} className="alpha-card p-7" data-testid={`fc-why-${c.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 20)}`}>
              <c.Icon className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
              <h3 className="mt-3 font-display text-lg font-semibold">{c.title}</h3>
              <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="max-w-2xl">
            <div className="alpha-overline">Intake process</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium">Designed around how you actually work.</h2>
          </div>
          <ol className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Submit referral", d: "Use the coordinator form, email intake@alphacommunitycare.com.au, or call 1300 380 692." },
              { n: "02", t: "Acknowledgement & scoping", d: "We respond within 2 business hours and schedule a discovery call to scope clinical, behavioural and housing needs." },
              { n: "03", t: "Match & onboard", d: "Clear capacity confirmation, transparent pricing, warm handover to the delivery team." },
            ].map((s) => (
              <li key={s.n} className="alpha-card p-6">
                <div className="font-display text-3xl font-semibold alpha-gradient-text">{s.n}</div>
                <div className="mt-2 font-display text-lg font-semibold">{s.t}</div>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-3 gap-5">
          <div className="alpha-card p-6">
            <div className="alpha-overline">Intake</div>
            <a href="mailto:intake@alphacommunitycare.com.au" className="mt-3 flex items-center gap-2 text-sm font-semibold"><Mail className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> intake@alphacommunitycare.com.au</a>
          </div>
          <div className="alpha-card p-6">
            <div className="alpha-overline">SIL enquiries</div>
            <a href="mailto:SIL@alphacommunitycare.com.au" className="mt-3 flex items-center gap-2 text-sm font-semibold"><Mail className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> SIL@alphacommunitycare.com.au</a>
          </div>
          <div className="alpha-card p-6">
            <div className="alpha-overline">Phone</div>
            <a href="tel:1300380692" className="mt-3 flex items-center gap-2 text-sm font-semibold"><Phone className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> 1300 380 692</a>
          </div>
        </div>
      </section>
    </div>
  );
}
